import {
  Event,
  EventService,
  Geogroup,
  GeogroupService,
  Partner,
} from '@geovelo-frontends/commons';
import { useQuery } from '@tanstack/react-query';

import { queryKeys } from './query-keys';

export interface IPartnerAvailableEvents {
  availableEvents: { current: Event[]; future: Event[] };
  hasChallenges: boolean;
}

async function fetchPartnerAvailableEvents(
  partner: Partner,
  geogroup: Geogroup,
): Promise<IPartnerAvailableEvents> {
  const [{ count }, runningEvents, futureEvents] = await Promise.all([
    GeogroupService.getChallenges(geogroup.id, {
      page: 1,
      pageSize: 1,
      state: ['running', 'future'],
      order: 'start_datetime',
    }),
    EventService.getAvailableEvents({
      partner,
      geogroupId: geogroup.id,
      page: 1,
      pageSize: 3,
      state: ['running'],
      automaticSubscription: 'CHALLENGE_EDITORIALIZED_SUBSCRIBER',
    }),
    EventService.getAvailableEvents({
      partner,
      geogroupId: geogroup.id,
      page: 1,
      pageSize: 3,
      state: ['future'],
      automaticSubscription: 'CHALLENGE_EDITORIALIZED_SUBSCRIBER',
    }),
  ]);

  return {
    hasChallenges: count > 0,
    availableEvents: { current: runningEvents, future: futureEvents },
  };
}

/** Every real-world consumer already re-checks `employee.isAdminOrAnimator` before using this data
 * (see pages/challenges/index.tsx's "Add" CTA) — pass the same condition in `enabled` so
 * holdings/plain employees don't fetch it needlessly. */
function usePartnerAvailableEventsQuery(
  partner: Partner | undefined | null,
  geogroup: Geogroup | undefined | null,
  { enabled = true }: { enabled?: boolean } = {},
) {
  return useQuery({
    queryKey: queryKeys.partnerAvailableEvents.detail(partner?.id, geogroup?.id),
    queryFn: () => fetchPartnerAvailableEvents(partner as Partner, geogroup as Geogroup),
    enabled: !!partner && !!geogroup && enabled,
  });
}

export default usePartnerAvailableEventsQuery;
